import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { DropzoneArea } from 'react-mui-dropzone';
import { trim } from 'lodash';
import papaparse from 'papaparse';
import CsvTable from './CsvTable';
import { getCountryCode3ByName } from 'utils/countries';
import { ICsvData, IInvalidData } from './index';
import { LoadingButton } from '@mui/lab';
import { UploadFile } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

const exampleColumns = [
  {
    title: 'Company Name',
    dataIndex: 'companyName',
    key: 'companyName',
  },
  {
    title: 'Country Code',
    dataIndex: 'countryCode3',
    key: 'countryCode3',
  },
  {
    title: 'Company Number',
    dataIndex: 'companyNumber',
    key: 'companyNumber',
  }
];

const invalidTableColumn = [
  {
    title: '#row',
    dataIndex: 'rowNumber',
    key: 'rowNumber',
  },
  {
    title: 'column',
    dataIndex: 'column',
    key: 'column',
  },
  {
    title: 'error',
    dataIndex: 'error',
    key: 'error',
  },
];


function validAndConvertCsvData(
  item: ICsvData
): Pick<IInvalidData, 'column' | 'error'> | void {
  if (item.companyNumber) {
    return;
  }
  item.companyName = trim(item.companyName);
  if (item.companyName.length < 2) {
    return {
      error: '"Company Name" does not seem valid',
      column: 'companyName',
    };
  }

  item.countryCode3 = trim(item.countryCode3) || '';

  // valid country code 3 digit letter and number
  if (!/\w{3}/.test(item.countryCode3)) {
    return {
      error: `"${item.countryCode3}" is not a valid country name or code`,
      column: 'country',
    };
  }
}

type IOnUpload = (list: ICsvData[]) => Promise<void>;

const steps = ['Select a CSV file', 'Validate and Preview'];

export default function CsvUploadSteps({
  onUpload,
  onFinish,
}: {
  onUpload: IOnUpload,
  onFinish: () => void;
}) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [file, setFile] = React.useState<File>();
  const [tabValue, setTabValue] = React.useState('1');
  const [readyCsvData, setReadyCsvData] = React.useState<ICsvData[]>([]);
  const [invalidCsvData, setInvalidCsvData] = React.useState<IInvalidData[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleUpload = async () => {
    // do the upload and get result
    setUploading(true);
    try {
      await onUpload(readyCsvData);
    } catch (e) {
      console.error(e);
      setUploadError(e instanceof Error ? e.message : 'Upload failed, please try again later');
    }
    setUploading(false);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const extractCompayListFromCsv = (csvString: string) => {
    /* example headers
     * company name, company country
     * or any column named "Company NationalID or Reg Number"
     */
    const res = papaparse.parse(csvString);
    const headers = res.data[0] as string[];
    const firstCell = headers[0].toLowerCase().trim();
    const hasHead = firstCell.startsWith('name') || firstCell.startsWith('company');
    const guessIndex = (name: string, defaultIndex: number): number => {
      const lowerHeaders= headers.map(i => i.toLocaleLowerCase());
      const idx = lowerHeaders.indexOf(name.toLocaleLowerCase());
      return idx >= 0 ? idx : defaultIndex;
    };
    const rows = res.data as string[][];
    const filterRow = rows.filter((row: string[]) => row.find(element => !!element)); // reject blank rows
    const regColIndex = guessIndex('Company NationalID or Reg Number', -1);
    const filterColumn: [string, string, string][] = filterRow.map((item) => {
      return [
        item[guessIndex('Company Name', 0)],
        item[guessIndex('Company Country', 1)],
        regColIndex >= 0 ? item[regColIndex] : '', // default 4 in the sample csv
      ];
    });
    if (hasHead) {
      // remove the header
      filterColumn.shift();
    }
    return {
      meta: {
        hasHead,
      },
      rows: filterColumn,
    };
  };

  const processContentString = (csvString: string) => {
    const companyNameMap: Map<string, number> = new Map();
    const companyNumberMap: Map<string, number> = new Map();


    const filteredData = extractCompayListFromCsv(csvString);

    const hasHead = filteredData.meta.hasHead;
    const rowNumberOffset = hasHead ? 2 : 1;
    const ready: ICsvData[] = [];
    const invalid: IInvalidData[] = [];
    filteredData.rows.forEach((item, index: number) => {
      const [companyName, countryInput, companyNumber] = item;
      const obj: ICsvData = {
        key: index,
        rowNumber: index + rowNumberOffset,
        companyName,
        countryCode3: getCountryCode3ByName(countryInput),
        companyNumber,
      };

      let err = validAndConvertCsvData(obj);

      // check repeat company name
      if (!err && obj.companyName && companyNameMap.has(obj.companyName)) {
        err = {
          column: 'company name',
          error: `"company name ${
            obj.companyName
          } is a duplication of row ${companyNameMap.get(obj.companyName) || 0}"`,
        };
      }

      // check if duplicated company number exists
      if (!err && obj.companyNumber && companyNumberMap.has(obj.companyNumber)) {
        err = {
          column: 'company number',
          error: `"company number ${
            obj.companyNumber
          } is a duplication of row ${companyNumberMap.get(obj.companyNumber) || 0}"`,
        };
      }

      if (!err) {
        ready.push(obj);
        if (obj.companyName) {
          companyNameMap.set(obj.companyName, obj.rowNumber);
        }
        if (obj.companyNumber) {
          companyNumberMap.set(obj.companyNumber, obj.rowNumber);
        }
      } else {
        invalid.push({
          key: index,
          rowNumber: index + rowNumberOffset,
          column: err.column,
          error: err.error,
        });
      }
    });

    setReadyCsvData(ready);
    setInvalidCsvData(invalid);
  };

  const readCsv = (f: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(f, 'utf8');
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
    });
  };

  const handleCsvSelected = async (files: File[]) => {
    const file = files[0];
    setFile(file);
    const csvString = await readCsv(file);
    processContentString(csvString);
  };

  const renderCSVUpload = () => {
    return (
      <React.Fragment>
        <Typography sx={{ mt: 2, mb: 1 }}>
          <Typography>
            1. Load the input Company List file in a CSV format. (Described below)
          </Typography>
          <Typography>
            2. The Input Format - Company Name, Country (ISO Code 3 / Name)
          </Typography>
        </Typography>

        <DropzoneArea
          acceptedFiles={['text/csv']}
          filesLimit={1}
          initialFiles={file ? [file] : []}
          useChipsForPreview
          dropzoneText="Drag and drop a CSV file here or click"
          onChange={(files) => handleCsvSelected(files)}
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={handleNext}>
            Next
          </Button>
        </Box>

      </React.Fragment>
    );
  };

  const renderCsvReadyData = () => {
    if (!readyCsvData.length) {
      return (
        <Box component="p" sx={{ p: 2, border: '1px solid grey', backgroundColor: '#f1f1f1' }}>
            There is no valid company entity found.
        </Box>
      );
    }
    return <CsvTable columns={exampleColumns} data={readyCsvData}/>;
  };

  const renderInvalidCsvData = () => {
    return <CsvTable columns={invalidTableColumn} data={invalidCsvData}/>;
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const renderPreview = () => {
    return (
      <React.Fragment>
        <Typography sx={{ mt: 2, mb: 1 }}>Once upload, all the companies will be verified by CoriolisESG.</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <TabContext value={tabValue}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChangeTab} aria-label="lab API tabs example">
                  <Tab label={`Ready to import (${readyCsvData.length})`} value="1" />
                  <Tab label={`Invalid (${invalidCsvData.length})`} value="2" />
                </TabList>
              </Box>
              <TabPanel value="1">
                {renderCsvReadyData()}
              </TabPanel>
              <TabPanel value="2">{renderInvalidCsvData()}</TabPanel>
            </TabContext>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />

          <LoadingButton
            disabled={Boolean(!readyCsvData?.length)}
            loading={uploading}
            loadingPosition="start"
            color="info"
            onClick={handleUpload}
            variant="contained"
            startIcon={<UploadFile />}>
            Upload
          </LoadingButton>

        </Box>
      </React.Fragment>
    );
  };

  const renderResult = () => {
    if (uploadError) {
      return (
        <React.Fragment>
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            There is an error occured: <strong>{uploadError}</strong>
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button onClick={onFinish}>
              Close
            </Button>
          </Box>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <Alert severity="success">
          <AlertTitle>Uploaded</AlertTitle>
          Click the button below to refresh the company list.
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={onFinish}>
            Refresh
          </Button>
        </Box>
      </React.Fragment>
    );
  };

  if (activeStep === steps.length) {
    return (
      <Box sx={{ width: '100%' }}>
        {renderResult()}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === 0 && renderCSVUpload()}
      {activeStep === 1 && renderPreview()}
    </Box>
  );
}
